import { Plugin } from 'ckeditor5/src/core.js';
import MgnlImageInsertViaUrlUI from './mgnlimageinsertviaurl';
import type { Editor } from 'ckeditor5/src/core';

/**
 * The Magnolia image insert plugin.
 * Enhanced with support for proper DAM browser integration.
 */
export default class MgnlImageInsert extends Plugin {
	/**
	 * The event name used to request the Magnolia DAM asset chooser
	 */
	public static MGNL_CHOOSE_ASSET_EVENT = 'chooseDAMAsset';

	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'MgnlImageInsert' as const;
	}

	/**
	 * @inheritDoc
	 */
	public static get requires() {
		return [ MgnlImageInsertViaUrlUI ] as const;
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		// Setup the event listener for resource selection
		this._setupMgnlLinkInterception();

		// Register our plugin for debugging
		console.log(
			'MgnlImageInsert plugin initialized with asset chooser integration'
		);
	}

	/**
	 * Sets up the interception of link resource selection to handle it as image selection
	 */
	private _setupMgnlLinkInterception(): void {
		const editor = this.editor;

		// Keep track of the image insertion state
		// Using WeakMap to avoid modifying editor object directly
		const editorStateMap = new WeakMap<Editor, boolean>();

		// Try to get the MgnlLink plugin
		try {
			const mgnlLinkPlugin = editor.plugins.get( 'MgnlLink' );
			if ( mgnlLinkPlugin ) {
				// Get the resource selected event name
				const resourceSelectedEvent =
					mgnlLinkPlugin.MGNL_RESOURCE_SELECTED_EVENT;

				// Add a high-priority listener for the resource selected event
				editor.listenTo(
					editor,
					resourceSelectedEvent,
					( evt, data ) => {
						// Check if the insertImage command exists and is enabled
						const insertImageCommand =
							editor.commands.get( 'insertImage' );
						const isImageCommand =
							insertImageCommand && insertImageCommand.isEnabled;

						// Check if we're currently in an image insert context
						if ( isImageCommand && editorStateMap.get( editor ) ) {
							// Stop the event to prevent the link plugin from handling it
							evt.stop();

							// Clear the flag
							editorStateMap.set( editor, false );

							// Process the asset data and insert the image
							this._handleSelectedAsset( data );
						}
					},
					{ priority: 'highest' }
				);

				// Listen for the button click to open DAM browser
				editor.on(
					'mgnlPluginButtonClicked:' +
						MgnlImageInsert.MGNL_CHOOSE_ASSET_EVENT,
					( evt, path ) => {
						// Set a flag that we're currently in an image insert operation
						editorStateMap.set( editor, true );

						// Use the MgnlLink plugin's event to open the DAM browser
						// Access MGNL_GET_LINK_EVENT safely
						const linkPlugin = mgnlLinkPlugin.constructor as any;
						const linkEventName =
							linkPlugin.MGNL_GET_LINK_EVENT || 'mgnlGetLink';

						editor.fire(
							'mgnlPluginButtonClicked:' + linkEventName,
							{ workspace: 'dam', path: path || '/' }
						);
					}
				);
			}
		} catch ( e ) {
			console.warn(
				'MgnlImageInsert: Failed to set up MgnlLink interception',
				e
			);
		}
	}

	/**
	 * Handles the selected asset from Magnolia DAM
	 */
	private _handleSelectedAsset( data: any ): void {
		const editor = this.editor;
		console.log( 'Handling selected asset:', data );

		// Process the data to get a proper image URL
		try {
			// First, try to parse the data if it's JSON in string form
			let parsedData = data;
			if ( typeof data === 'string' ) {
				try {
					parsedData = JSON.parse( data );
				} catch ( e ) {
					console.log( 'Data is not a JSON string' );
				}
			}

			// Extract the asset identifier (UUID) and filename
			let assetId = '';
			let fileName = '';

			if ( typeof parsedData === 'object' && parsedData ) {
				console.log( 'Processing object data:', parsedData );

				// Get the asset identifier (UUID)
				if ( parsedData.identifier ) {
					assetId = parsedData.identifier;
					console.log( 'Extracted asset ID:', assetId );
				}

				// Get the filename from path or fileName property
				if ( parsedData.path ) {
					const pathParts = parsedData.path.split( '/' );
					fileName = pathParts[ pathParts.length - 1 ];
					console.log( 'Extracted filename from path:', fileName );
				} else if ( parsedData.fileName ) {
					fileName = parsedData.fileName;
					console.log( 'Using fileName property:', fileName );
				} else if ( parsedData.name ) {
					fileName = parsedData.name;
					console.log( 'Using name property:', fileName );
				}
			} else if ( typeof data === 'string' ) {
				if ( data.includes( '${link:' ) ) {
					// Extract from link format: ${link:{workspace:{dam},path:{/path/to/file},identifier:{uuid}}}
					const identifierMatch = data.match( /identifier:\{([^}]*)\}/ );
					const pathMatch = data.match( /path:\{([^}]*)\}/ );

					if ( identifierMatch && identifierMatch[ 1 ] ) {
						assetId = identifierMatch[ 1 ];
						console.log( 'Extracted asset ID from link string:', assetId );
					}

					if ( pathMatch && pathMatch[ 1 ] ) {
						const pathParts = pathMatch[ 1 ].split( '/' );
						fileName = pathParts[ pathParts.length - 1 ];
						console.log( 'Extracted filename from link string:', fileName );
					}
				}
			}

			// Construct the new Magnolia 6.3.2 DAM URL format: ${origin}/dam/jcr:UUID/fileName
			let imageSrc = '';
			if ( assetId && fileName ) {
				// Use the new DAM servlet format with origin for absolute URLs
				const origin = window.location.origin;
				console.log( '🚀 ~ MgnlImageInsert ~ _handleSelectedAsset ~ origin:', origin );
				imageSrc = `${ origin }/dam/jcr:${ assetId }/${ fileName }`;
				console.log( 'Constructed DAM URL (new format):', imageSrc );
			} else {
				console.warn( 'Could not extract both asset ID and filename from data:', { assetId, fileName } );
				return;
			}

			// Insert the image
			if ( imageSrc ) {
				const insertImageCommand = editor.commands.get( 'insertImage' );
				if ( insertImageCommand && insertImageCommand.isEnabled ) {
					editor.execute( 'insertImage', { source: imageSrc } );
					console.log( 'Image inserted successfully with URL:', imageSrc );
				} else {
					console.warn(
						'insertImage command not available or not enabled'
					);
				}
			} else {
				console.warn( 'Could not determine image source from data' );
			}
		} catch ( e ) {
			console.error( 'Error processing asset data:', e );
		}
	}

	/**
	 * Gets the absolute base URL including protocol and domain
	 */
	private _getAbsoluteBaseUrl(): string {
		const origin = window.location.origin; // e.g., "http://localhost:8080"

		// First check if we're in the backend environment
		if (
			window.location.href.includes(
				'/backend-valeo-pass-cms-webapp-1.0.0/'
			)
		) {
			return origin + '/backend-valeo-pass-cms-webapp-1.0.0';
		}
		if ( window.location.href.includes( '/.magnolia/admincentral' ) ) {
			// Return just the origin without admin paths
			return origin;
		}

		// Check if there's a configured base URL for frontend
		if ( ( window as any ).MgnlVars && ( window as any ).MgnlVars.baseUrl ) {
			// Make sure it's absolute
			return this._makeUrlAbsolute(
				( window as any ).MgnlVars.baseUrl,
				origin
			);
		}

		// Try to find the base context path from meta tags
		const contextPathMeta = document.querySelector(
			'meta[name="magnolia-contextpath"]'
		);
		if ( contextPathMeta && ( contextPathMeta as HTMLMetaElement ).content ) {
			return this._makeUrlAbsolute(
				( contextPathMeta as HTMLMetaElement ).content,
				origin
			);
		}

		// Look for common Magnolia contextPath patterns in the URL
		const pathMatch = window.location.pathname.match( /^(\/[^/]+\/[^/]+)/ );
		if ( pathMatch && pathMatch[ 1 ] ) {
			return origin + pathMatch[ 1 ];
		}

		// If we can't determine a specific context path, just use the origin
		return origin;
	}

	/**
	 * Makes a URL absolute by adding the base URL if needed
	 */
	private _makeUrlAbsolute( url: string, baseUrl: string ): string {
		if ( !url ) {
			return baseUrl;
		}

		// If it's already absolute, return it
		if (
			url.startsWith( 'http://' ) ||
			url.startsWith( 'https://' ) ||
			url.startsWith( '//' )
		) {
			return url;
		}

		// If it's a root-relative URL (starts with /)
		if ( url.startsWith( '/' ) ) {
			// If baseUrl ends with / and url starts with /, avoid double slash
			if ( baseUrl.endsWith( '/' ) ) {
				return baseUrl + url.substring( 1 );
			}
			return baseUrl + url;
		}

		// If it's a relative URL (doesn't start with /)
		// Make sure baseUrl ends with /
		if ( !baseUrl.endsWith( '/' ) ) {
			baseUrl = baseUrl + '/';
		}
		return baseUrl + url;
	}
}
